import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Breadcrumb,
  Button,
  Descriptions,
  Empty,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { BackButton, PageContainer } from '@/ui/components';
import { useTranslate } from '@/translations';
import { useProductImageryQuery } from '@/api/ProductImages/hooks';
import { productImages } from '@/api/ProductImages/ProductImages';
import type {
  GenerationRecord,
  ImageDescriptor,
} from '@/api/ProductImages/types';
import {
  GenerationButton,
  activeGeneration,
  ImageryAccess,
  ImageryError,
  ImageryStatus,
  imageryRoot,
} from './ProductImages';
import { ImageryContent } from './ProductImages.styled';
import { GenerateImageDialog } from './GenerateImageDialog';
import { PrivateImage } from './PrivateImage';
import { formatImageryTime, shortId } from './GenerationRecord';
import { ImageQualityTag } from './ImageQualityTag';
import {
  generationOutput,
  generationStyleMessage,
  generationVariantKey,
  activeVariantGeneration,
} from '@/api/ProductImages/generationVariants';
import { VariantImages } from '@/modules/ProductImages/VariantImage';
import { variantImageMessage } from '@/modules/ProductImages/variantImageMessages';
import { SuspendImageDialog } from '@/modules/ProductImages/SuspendImageDialog';
import { ImageSuspensionNotice } from '@/modules/ProductImages/ImageSuspensionNotice';

const ProductImageDetail = () => {
  const { t } = useTranslate();
  const { productId = '' } = useParams();
  const [params] = useSearchParams();
  const [generating, setGenerating] = useState(false);
  const [suspendingRevision, setSuspendingRevision] = useState<string | null>(
    null,
  );
  const result = useProductImageryQuery(
    ['product', productId],
    () => productImages.product(Number(productId)),
    (detail) =>
      activeGeneration(detail.product.generationStatus) ||
      activeVariantGeneration(detail.currentImage) ||
      detail.generations.some((generation) =>
        activeGeneration(generation.status),
      )
        ? 5_000
        : false,
  );
  if (result.isError)
    return (
      <ImageryError error={result.error} retry={() => void result.refetch()} />
    );
  if (!result.data) return <Spin />;
  const { product, currentImage, generations, revisions } = result.data;
  const styleLabel = (record: GenerationRecord) => {
    const key = generationVariantKey(record);
    const message = generationStyleMessage(key);
    return message ? t(message) : key;
  };
  const backParams = new URLSearchParams(params);
  backParams.set('tab', 'products');
  const back = `${imageryRoot}?${backParams}`;
  return (
    <>
      <BackButton title={t('imagery.products')} linkTo={back} />
      <Breadcrumb
        className="mt-3"
        items={[
          { title: <Link to={back}>{t('imagery.products')}</Link> },
          { title: product.productCode },
        ]}
      />
      <div className="imagery-heading">
        <div>
          <h2>
            {product.productCode} / {product.productDescription}
          </h2>
          <div className="mt-2">
            {product.generationStatus === 'Published' && !currentImage ? (
              <Tag>{t('imagery.noImage')}</Tag>
            ) : (
              <ImageryStatus status={product.generationStatus} />
            )}
          </div>
        </div>
        <div className="imagery-actions">
          <Button
            onClick={() => void result.refetch()}
            loading={result.isFetching}
          >
            {t('imagery.refresh')}
          </Button>
          <GenerationButton
            product={{ ...product, currentImage }}
            onClick={() => setGenerating(true)}
          />
          {currentImage?.sourceKind === 'Generated' &&
          currentImage.canSuspend !== undefined ? (
            <Tooltip
              title={
                !currentImage.canSuspend
                  ? variantImageMessage(
                      t,
                      currentImage.suspensionUnavailableReason,
                      'imagery.suspendUnavailable',
                    )
                  : undefined
              }
            >
              <Button
                danger
                disabled={!currentImage.canSuspend}
                onClick={() => setSuspendingRevision(currentImage.assetId)}
              >
                {t('imagery.suspendImage')}
              </Button>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div className="imagery-detail-grid imagery-product-detail">
        <div
          className={
            currentImage?.representationKind === 'Outline'
              ? 'imagery-image-pair'
              : undefined
          }
        >
          <section>
            <h3>
              {t(
                currentImage?.representationKind === 'Outline'
                  ? 'imagery.outlineImage'
                  : 'imagery.currentImage',
              )}
            </h3>
            {currentImage?.representationKind &&
            currentImage.representationKind !== 'Outline' ? (
              <p>{currentImage.representationKind}</p>
            ) : null}
            {currentImage?.sourceKind === 'Generated' ? (
              <div className="imagery-image-output">
                <PrivateImage
                  src={currentImage.contentUrl}
                  assetId={currentImage.assetId}
                />
                <ImageQualityTag image={currentImage} />
              </div>
            ) : (
              <div className="imagery-image">
                <Empty
                  description={t(
                    currentImage
                      ? 'imagery.unsupportedSource'
                      : 'imagery.noImage',
                  )}
                />
              </div>
            )}
          </section>
          {currentImage?.representationKind === 'Outline' ? (
            <VariantImages
              image={currentImage}
              productId={product.productId}
              productCode={product.productCode}
              primaryActive={activeGeneration(product.generationStatus)}
            />
          ) : null}
        </div>
        <div>
          <h3>{t('imagery.productInformation')}</h3>
          <Descriptions
            bordered
            size="small"
            column={1}
            items={[
              {
                key: 'customer',
                label: t('imagery.customer'),
                children: product.customerName,
              },
              {
                key: 'code',
                label: t('imagery.productCode'),
                children: product.productCode,
              },
              {
                key: 'manufacturer',
                label: t('imagery.manufacturer'),
                children: product.manufacturerName || '-',
              },
              {
                key: 'description',
                label: t('imagery.productDescription'),
                children: product.productDescription,
              },
              {
                key: 'stock',
                label: t('imagery.qualifyingStock'),
                children: product.qualifyingStockCount,
              },
            ]}
          />
          <h3 className="mt-4">{t('imagery.imageInformation')}</h3>
          {currentImage ? (
            <Descriptions
              bordered
              size="small"
              column={1}
              items={[
                {
                  key: 'revision',
                  label: t('imagery.currentRevision'),
                  children: t('imagery.revision', {
                    revision: currentImage.revision,
                  }),
                },
                {
                  key: 'source',
                  label: t('imagery.source'),
                  children:
                    currentImage.sourceKind === 'Generated'
                      ? t('imagery.generated')
                      : currentImage.sourceKind,
                },
                {
                  key: 'published',
                  label: t('imagery.publishedAt'),
                  children: formatImageryTime(currentImage.createdAt),
                },
                ...(currentImage.generationId &&
                currentImage.qualityScore != null
                  ? [
                      {
                        key: 'score',
                        label: t('imagery.score'),
                        children: `${currentImage.qualityScore}/100`,
                      },
                    ]
                  : []),
              ]}
            />
          ) : (
            <span className="text-secondary">{t('imagery.noImage')}</span>
          )}
        </div>
      </div>
      <section className="imagery-section">
        <h3>{t('imagery.imageHistory')}</h3>
        <Table<ImageDescriptor>
          size="middle"
          rowKey="assetId"
          dataSource={revisions}
          scroll={{ x: 750 }}
          pagination={revisions.length > 10 ? { pageSize: 10 } : false}
          columns={[
            {
              title: t('imagery.currentImage'),
              width: 140,
              render: (_, revision) =>
                revision.sourceKind === 'Generated' ? (
                  <PrivateImage
                    src={revision.thumbnailUrl ?? revision.contentUrl}
                    assetId={revision.assetId}
                    thumbnail
                    label={t(
                      revision.assetId === currentImage?.assetId
                        ? 'imagery.currentRevision'
                        : 'imagery.historical',
                    )}
                  />
                ) : (
                  '-'
                ),
            },
            {
              title: t('imagery.variantResults'),
              width: 155,
              render: (_, revision) =>
                revision.representationKind === 'Outline' ? (
                  <VariantImages image={revision} thumbnail />
                ) : (
                  '-'
                ),
            },
            {
              title: t('imagery.imageVersions'),
              width: 170,
              render: (_, revision) => (
                <>
                  {t('imagery.revision', { revision: revision.revision })}
                  {revision.assetId === currentImage?.assetId ? (
                    <div>
                      <Tag color="success">{t('imagery.currentRevision')}</Tag>
                    </div>
                  ) : null}
                  <div>
                    <ImageQualityTag image={revision} />
                  </div>
                  <ImageSuspensionNotice image={revision} />
                </>
              ),
            },
            { title: t('imagery.source'), dataIndex: 'sourceKind', width: 130 },
            {
              title: t('imagery.publishedAt'),
              dataIndex: 'createdAt',
              render: formatImageryTime,
              width: 190,
            },
            {
              title: t('imagery.run'),
              width: 150,
              render: (_, revision) =>
                revision.generationId ? (
                  <Link
                    className="imagery-record-id"
                    to={`${imageryRoot}/generations/${revision.generationId}?${params}`}
                  >
                    RUN {shortId(revision.generationId)}
                  </Link>
                ) : (
                  '-'
                ),
            },
          ]}
        />
      </section>
      <section className="imagery-section">
        <h3>{t('imagery.generationHistory')}</h3>
        <Table<GenerationRecord>
          size="middle"
          rowKey="id"
          dataSource={generations}
          scroll={{ x: 1400 }}
          pagination={generations.length > 10 ? { pageSize: 10 } : false}
          columns={[
            {
              title: t('imagery.run'),
              width: 145,
              render: (_, generation) => (
                <Link
                  className="imagery-record-id"
                  to={`${imageryRoot}/generations/${generation.id}?${params}`}
                >
                  RUN {shortId(generation.id)}
                </Link>
              ),
            },
            {
              title: t('imagery.style'),
              width: 130,
              render: (_, generation) => styleLabel(generation),
            },
            {
              title: t('imagery.sourceRevision'),
              width: 145,
              render: (_, generation) => {
                if (!generation.sourceRevisionId) return '-';
                const source = revisions.find(
                  (revision) =>
                    revision.assetId === generation.sourceRevisionId,
                );
                const revision = generation.sourceRevision ?? source?.revision;
                const label =
                  revision == null
                    ? shortId(generation.sourceRevisionId)
                    : t('imagery.revision', { revision });
                const parentId = generation.parentGenerationId;
                return parentId ? (
                  <Link to={`${imageryRoot}/generations/${parentId}?${params}`}>
                    {label}
                  </Link>
                ) : (
                  label
                );
              },
            },
            {
              title: t('imagery.output'),
              width: 130,
              render: (_, generation) => {
                const asset = generationOutput(generation);
                const revision = generation.outputImage;
                return asset ? (
                  <PrivateImage
                    generationId={asset.assetId}
                    src={asset.thumbnailUrl ?? asset.contentUrl}
                    thumbnail
                    label={styleLabel(generation)}
                  />
                ) : revision?.sourceKind === 'Generated' ? (
                  <PrivateImage
                    assetId={revision.assetId}
                    src={revision.thumbnailUrl ?? revision.contentUrl}
                    thumbnail
                    label={styleLabel(generation)}
                  />
                ) : (
                  '-'
                );
              },
            },
            {
              title: t('imagery.result'),
              dataIndex: 'status',
              width: 115,
              render: (status) => <ImageryStatus status={status} />,
            },
            {
              title: t('imagery.requestedAt'),
              dataIndex: 'requestedAt',
              render: formatImageryTime,
              width: 190,
            },
            {
              title: t('imagery.resultReason'),
              width: 260,
              render: (_, generation) =>
                generation.errorCode || generation.errorMessage ? (
                  <>
                    {variantImageMessage(
                      t,
                      generation.errorCode || generation.errorMessage,
                    )}
                    {generation.errorMessage &&
                    generation.errorMessage !==
                      variantImageMessage(
                        t,
                        generation.errorCode || generation.errorMessage,
                      ) ? (
                      <div>{generation.errorMessage}</div>
                    ) : null}
                  </>
                ) : (
                  generation.errorMessage ||
                  generation.errorCode ||
                  generation.reason ||
                  '-'
                ),
            },
            {
              title: t('imagery.origin'),
              width: 140,
              render: (_, generation) => (
                <>
                  {generation.triggerOrigin || '-'}
                  {generation.batchId ? (
                    <div>
                      <Link
                        to={`${imageryRoot}/batches/${generation.batchId}?${params}`}
                      >
                        {shortId(generation.batchId)}
                      </Link>
                    </div>
                  ) : null}
                </>
              ),
            },
            {
              title: t('imagery.requestedBy'),
              width: 155,
              render: (_, generation) => generation.requestedBy || '-',
            },
            {
              title: t('imagery.score'),
              width: 110,
              render: (_, generation) =>
                typeof generation.evaluation?.score === 'number'
                  ? `${generation.evaluation.score}/100`
                  : generation.candidates
                      .map(
                        (candidate) => candidate.score ?? t('imagery.unknown'),
                      )
                      .join(' / ') || t('imagery.unknown'),
            },
            {
              title: t('imagery.estimatedCost'),
              width: 150,
              render: (_, generation) =>
                generation.estimatedCost == null
                  ? t('imagery.unknown')
                  : `$${generation.estimatedCost.toFixed(4)}`,
            },
          ]}
        />
      </section>
      {generating ? (
        <GenerateImageDialog
          product={{ ...product, currentImage }}
          onClose={() => setGenerating(false)}
        />
      ) : null}
      {suspendingRevision ? (
        <SuspendImageDialog
          productId={product.productId}
          revisionId={suspendingRevision}
          onClose={() => setSuspendingRevision(null)}
        />
      ) : null}
    </>
  );
};

export const ProductImagePage = () => (
  <ImageryAccess>
    <PageContainer>
      <ImageryContent>
        <ProductImageDetail />
      </ImageryContent>
    </PageContainer>
  </ImageryAccess>
);
